"use client";

import * as React from "react";
import { Link } from "react-router";
import { Check, ChevronLeft, ChevronRight, Minus } from "lucide-react";

import { Badge } from "@/app/components/ui/badge";
import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/components/ui/utils";
import {
  compareMatrixBySection,
  ENTERPRISE_PERSONA_BLURB,
  PLAN_TIER_LABELS,
  PLAN_TIER_TAGLINES,
  PRO_PERSONA_BLURB,
  tierIncludesFeature,
  type PlanFeatureRow,
  type PlanTier,
} from "@/app/data/planCatalog";
import { Seo } from "../components/Seo";

const TIERS: PlanTier[] = ["essential", "pro", "enterprise"];

const planTierCtaClassName =
  "border border-teal-500/40 bg-[#008080] text-white shadow-md shadow-teal-900/25 transition-colors " +
  "hover:bg-[#006666] hover:shadow-lg hover:shadow-teal-900/30 active:bg-[#005858]";

function CompareCell({ row, tier }: { row: PlanFeatureRow; tier: PlanTier }) {
  const value = row.compareValues?.[tier];
  if (value !== undefined) {
    return (
      <td className="px-4 py-3 text-center align-middle">
        <span
          className={cn(
            "text-sm font-medium leading-snug",
            value === "-" ? "text-slate-500" : "text-slate-100",
          )}
        >
          {value}
        </span>
      </td>
    );
  }

  const included = tierIncludesFeature(row, tier);
  const note = row.compareNote?.[tier];

  if (!included) {
    return (
      <td className="px-4 py-3 text-center align-middle">
        <Minus className="mx-auto size-4 text-slate-600" aria-hidden />
        <span className="sr-only">Not included</span>
      </td>
    );
  }

  return (
    <td className="px-4 py-3 text-center align-middle">
      <Check className="mx-auto size-5 stroke-[2.5] text-teal-400" aria-hidden />
      {note ? <p className="mt-1 text-[11px] text-slate-500">{note}</p> : null}
      <span className="sr-only">Included{note ? `: ${note}` : ""}</span>
    </td>
  );
}

export function ComparePlans() {
  const groups = compareMatrixBySection();

  return (
    <>
      <Seo
        title="Compare plans"
        description="Compare DeskReplay Essential, Pro, and Enterprise: capture, governance, AI intelligence, storage, identity, and deployment options side by side."
        canonicalPath="/pricing/compare"
      />
      <div className="min-h-screen bg-slate-950 pt-28 pb-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/pricing"
            className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-teal-400 hover:text-teal-300"
          >
            <ChevronLeft className="size-4" />
            Back to pricing
          </Link>

          <div className="mb-10 max-w-3xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-teal-400">
              Compare plans
            </p>
            <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-slate-100">
              Essential, Pro, and Enterprise
            </h1>
            <p className="text-lg leading-relaxed text-on-dark-muted">
              Full feature matrix for baseline compliance, managed accountability, and sovereign
              deployments. Storage limits, retention periods, and capture quality follow your plan tier.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              Enterprise Advanced RBAC can assign each administrator an evidence scope (desktop sessions only,
              CCTV only, or both), independent of role lane.
            </p>
          </div>

          <div className="mb-10 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-400">Pro</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{PRO_PERSONA_BLURB}</p>
            </div>
            <div className="rounded-xl border border-teal-600/25 bg-teal-950/20 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-300">
                Enterprise
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{ENTERPRISE_PERSONA_BLURB}</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/30">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80">
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-slate-950/95 px-4 py-4 font-semibold text-slate-200"
                  >
                    Feature
                  </th>
                  {TIERS.map((tier) => (
                    <th
                      key={tier}
                      scope="col"
                      className={cn(
                        "min-w-[140px] px-4 py-4 text-center font-semibold",
                        tier === "enterprise" ? "text-teal-200" : "text-slate-200",
                      )}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{PLAN_TIER_LABELS[tier].toUpperCase()}</span>
                        <Badge
                          variant="outline"
                          className="border-slate-600 text-[10px] font-medium text-slate-400"
                        >
                          {PLAN_TIER_TAGLINES[tier]}
                        </Badge>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map(({ section, rows }) => (
                  <React.Fragment key={section.id}>
                    <tr className="border-b border-slate-800/80">
                      <td
                        colSpan={4}
                        className="sticky left-0 bg-slate-900/90 px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-teal-400"
                      >
                        {section.title}
                      </td>
                    </tr>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-slate-800/50 hover:bg-slate-800/20"
                      >
                        <th
                          scope="row"
                          className="sticky left-0 z-[1] bg-slate-950/95 px-4 py-3 font-normal text-slate-300"
                        >
                          {row.label}
                        </th>
                        {TIERS.map((tier) => (
                          <CompareCell key={tier} row={row} tier={tier} />
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr className="bg-slate-950/50">
                  <td className="px-4 py-5" />
                  {TIERS.map((tier) => (
                    <td key={tier} className="px-4 py-5 text-center">
                      <Button size="sm" className={planTierCtaClassName} asChild>
                        <Link to={`/request-demo?plan=${tier}`}>
                          Get started
                          <ChevronRight className="size-3.5" />
                        </Link>
                      </Button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-xs leading-relaxed text-on-dark-dim">
            * Enterprise self-hosted: unlimited storage is the capacity you provision (disks, object
            storage, SAN, and so on). Recordings stay in your environment.
          </p>
        </div>
      </div>
    </>
  );
}
